import * as React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { STREICH_FARBEN, type BearbeitungAktion, type PdfRect, type StreichEintrag, type StreichFarbe } from "./pdfBearbeitung";
import { textRechteckeDerSelection } from "./auswahlText";
import { cssZuPdfRect, normalisiere } from "./pdfKoordinaten";
import { inhaltsBox, type SeitenAnsichtLike, type ViewerLike } from "./EntfernenEbene";
import { alleDokumente, alleFenster } from "../../windowManager/browserFenster/fensterRegister";

/**
 * Durchstreichen-Ebene (T-363, User 22.10.2026): Text auswählen → ein Strich
 * läuft durch die Mitte, der Text bleibt lesbar. Auf Scans ohne Textebene
 * (oder mit Alt auch über Text) zieht man eine Linie auf — sie rastet
 * waagerecht bzw. senkrecht ein.
 *
 * Hier wird nur der Stand geführt und als Vorschau gezeichnet; ins PDF kommen
 * die Striche erst beim Speichern (`streichungSchreiben.ts`, `/StrikeOut`).
 *
 * Wie die Entfernen-Ebene hängt sie per Portal in den `.page`-Divs von PDF.js
 * und hängt sich nach jedem Neuzeichnen wieder ein.
 */

/** Kürzer als das (CSS-px) gilt eine aufgezogene Linie als Klick. */
const MIN_LAENGE = 6;
/** Strichstärke einer aufgezogenen Linie (pt). */
const LINIE_PT = 1.5;
/** Halbe Höhe des Umrisses um eine aufgezogene Linie (pt) — für die QuadPoints. */
const LINIE_BAND_PT = 4;

const RENDER_EREIGNISSE = ["pagerendered", "textlayerrendered", "scalechanging", "rotationchanging", "pagesinit"];

let zaehler = 0;
const neueId = () => `s${Date.now().toString(36)}${(++zaehler).toString(36)}`;

const r2 = (n: number) => Math.round(n * 100) / 100;

/** Strichstärke zur Zeilendicke: kräftig genug zum Sehen, nie fetter als 3 pt. */
export const staerkeFuer = (dickePt: number) => r2(Math.min(3, Math.max(0.75, dickePt * 0.07)));

/** Die aktuelle, nicht leere Textauswahl im Viewer — sonst `null`. */
const auswahlImViewer = (container: HTMLElement | null): Selection | null => {
    const sel = window.getSelection?.();
    if (!container || !sel || sel.isCollapsed || sel.rangeCount === 0) return null;
    const drin = (n: Node | null) => !!n && container.contains(n);
    return drin(sel.anchorNode) || drin(sel.focusNode) ? sel : null;
};

/** CSS-Punkt (relativ zur Seite) → Userspace. */
const zuPdf = (v: SeitenAnsichtLike["viewport"], x: number, y: number): [number, number] => {
    const [px, py] = v.convertToPdfPoint(x, y);
    return [r2(px), r2(py)];
};

/**
 * Ein Zeilenstück einer Textauswahl (CSS, relativ zur Seite) → Durchstreichung.
 * Der Strich läuft auf dem Bildschirm entlang der längeren Kante — so trifft er
 * auch Text, der auf einer gedrehten Seite senkrecht steht.
 */
export function strichAusKasten(
    v: SeitenAnsichtLike["viewport"],
    k: { x0: number; y0: number; x1: number; y1: number },
): Pick<StreichEintrag, "rect" | "linie" | "staerke"> {
    const waagerecht = k.x1 - k.x0 >= k.y1 - k.y0;
    const mx = (k.x0 + k.x1) / 2;
    const my = (k.y0 + k.y1) / 2;
    const [a, b] = waagerecht ? [zuPdf(v, k.x0, my), zuPdf(v, k.x1, my)] : [zuPdf(v, mx, k.y0), zuPdf(v, mx, k.y1)];
    const rect = cssZuPdfRect(v, k.x0, k.y0, k.x1, k.y1);
    const dicke = Math.min(rect[2] - rect[0], rect[3] - rect[1]);
    return { rect, linie: [a[0], a[1], b[0], b[1]], staerke: staerkeFuer(dicke) };
}

/** Aufgezogene Linie (CSS, schon eingerastet) → Durchstreichung mit Band für die QuadPoints. */
export function strichAusLinie(
    v: SeitenAnsichtLike["viewport"],
    p: { x0: number; y0: number; x1: number; y1: number },
): Pick<StreichEintrag, "rect" | "linie" | "staerke"> {
    const a = zuPdf(v, p.x0, p.y0);
    const b = zuPdf(v, p.x1, p.y1);
    const [x0, y0, x1, y1] = normalisiere([a[0], a[1], b[0], b[1]]);
    // Waagerecht im Userspace → Band nach oben/unten, sonst nach links/rechts.
    const waagerecht = x1 - x0 >= y1 - y0;
    const rect: PdfRect = waagerecht
        ? [x0, r2(y0 - LINIE_BAND_PT), x1, r2(y1 + LINIE_BAND_PT)]
        : [r2(x0 - LINIE_BAND_PT), y0, r2(x1 + LINIE_BAND_PT), y1];
    return { rect, linie: [a[0], a[1], b[0], b[1]], staerke: LINIE_PT };
}

/** Rastet den Endpunkt auf die vorherrschende Richtung ein (waagerecht oder senkrecht). */
export const einrasten = (x0: number, y0: number, x1: number, y1: number) =>
    Math.abs(x1 - x0) >= Math.abs(y1 - y0) ? { x0, y0, x1, y1: y0 } : { x0, y0, x1: x0, y1 };

type Zug = { seite: number; x0: number; y0: number; x1: number; y1: number };

const stil = {
    kreuz: {
        position: "absolute",
        width: 18,
        height: 18,
        borderRadius: 9,
        border: "1px solid rgba(60, 60, 60, 0.9)",
        background: "#fff",
        color: "rgb(60, 60, 60)",
        fontSize: 12,
        lineHeight: "16px",
        padding: 0,
        cursor: "pointer",
        pointerEvents: "auto",
        transform: "translate(-50%, -50%)",
    } as React.CSSProperties,
};

export const StreichEbene: React.FC<{
    viewer: ViewerLike | null;
    /** Scrollender Container des Viewers — hier beginnt das Aufziehen. */
    container: HTMLElement | null;
    /** Werkzeug „Durchstreichen“ gewählt? Striche sind immer sichtbar, gestrichen wird nur aktiv. */
    aktiv: boolean;
    eintraege: readonly StreichEintrag[];
    farbe: StreichFarbe;
    dispatch: (a: BearbeitungAktion) => void;
}> = ({ viewer, container, aktiv, eintraege, farbe, dispatch }) => {
    const [, neuZeichnen] = React.useReducer((n: number) => n + 1, 0);
    const [zug, setZug] = React.useState<Zug | null>(null);
    const hosts = React.useRef(new Map<number, HTMLDivElement>());

    React.useEffect(() => {
        if (!viewer) return;
        const bus = viewer.eventBus;
        const fn = () => neuZeichnen();
        for (const e of RENDER_EREIGNISSE) bus.on(e, fn);
        return () => {
            for (const e of RENDER_EREIGNISSE) bus.off(e, fn);
        };
    }, [viewer]);

    React.useEffect(() => {
        const map = hosts.current;
        return () => {
            for (const h of map.values()) h.remove();
            map.clear();
        };
    }, []);

    const seitenAnsicht = React.useCallback(
        (i: number): SeitenAnsichtLike | null => (viewer ? viewer.getPageView(i) ?? null : null),
        [viewer],
    );

    const hostFuer = (i: number): HTMLDivElement => {
        let host = hosts.current.get(i);
        if (!host) {
            host = document.createElement("div");
            host.className = "kmvs-streich-ebene";
            Object.assign(host.style, { position: "absolute", inset: "0", pointerEvents: "none", zIndex: "21" });
            hosts.current.set(i, host);
        }
        return host;
    };
    const benutzteSeiten = React.useRef(new Set<number>());

    React.useLayoutEffect(() => {
        for (const [i, host] of hosts.current) {
            const div = benutzteSeiten.current.has(i) ? seitenAnsicht(i)?.div : null;
            if (div && host.parentNode !== div) div.appendChild(host);
            else if (!div && host.parentNode) host.remove();
        }
    });

    // ─── Streichen per Textauswahl ──────────────────────────────────────────

    const farbeRef = React.useRef(farbe);
    farbeRef.current = farbe;

    const streicheAuswahl = React.useCallback(
        (sel: Selection) => {
            if (!viewer) return;
            const kaesten = textRechteckeDerSelection(sel);
            const gruppe = neueId();
            const neu: StreichEintrag[] = [];
            for (let i = 0; i < viewer.pagesCount; i++) {
                const ansicht = seitenAnsicht(i);
                if (!ansicht?.div) continue;
                const box = inhaltsBox(ansicht.div);
                // Kästen ganzer Ebenen/Seiten (seitenübergreifende Auswahl) verwerfen.
                const maxDicke = 0.1 * Math.max(box.width, box.height);
                for (const r of kaesten) {
                    const mx = r.left + r.width / 2;
                    const my = r.top + r.height / 2;
                    if (mx < box.left || mx > box.right || my < box.top || my > box.bottom) continue;
                    if (Math.min(r.width, r.height) > maxDicke || r.width <= 0 || r.height <= 0) continue;
                    const k = {
                        x0: Math.max(r.left - box.left, 0),
                        y0: Math.max(r.top - box.top, 0),
                        x1: Math.min(r.right - box.left, box.width),
                        y1: Math.min(r.bottom - box.top, box.height),
                    };
                    neu.push({ id: neueId(), seite: i, farbe: farbeRef.current, quelle: "text", gruppe, ...strichAusKasten(ansicht.viewport, k) });
                }
            }
            sel.removeAllRanges();
            if (neu.length) dispatch({ typ: "streichenHinzu", eintraege: neu });
        },
        [dispatch, seitenAnsicht, viewer],
    );

    // Loslassen nach einer Auswahl streicht sofort (wie „Markieren“). Beim
    // Einschalten des Werkzeugs wird eine schon bestehende Auswahl gestrichen.
    React.useEffect(() => {
        if (!aktiv || !viewer) return;
        const vorher = auswahlImViewer(container);
        if (vorher) streicheAuswahl(vorher);
        const onUp = () => {
            // Erst nach dem Ereignis steht die Auswahl fest.
            window.setTimeout(() => {
                const sel = auswahlImViewer(container);
                if (sel) streicheAuswahl(sel);
            }, 0);
        };
        alleDokumente.addEventListener("pointerup", onUp);
        return () => alleDokumente.removeEventListener("pointerup", onUp);
    }, [aktiv, container, streicheAuswahl, viewer]);

    // ─── Linie aufziehen (Scans, oder mit Alt über Text) ────────────────────

    React.useEffect(() => {
        if (!container) return;
        const vorher = container.style.cursor;
        if (aktiv) container.style.cursor = "crosshair";
        return () => {
            container.style.cursor = vorher;
        };
    }, [aktiv, container]);

    React.useEffect(() => {
        if (!container || !aktiv || !viewer) return;
        let zugBeenden: (() => void) | null = null;
        const onDown = (e: PointerEvent) => {
            if (e.button !== 0) return;
            zugBeenden?.();
            const ziel = e.target as HTMLElement | null;
            if (!ziel || typeof ziel.closest !== "function") return;
            if (ziel.closest(".kmvs-streich-ebene button, .kmvs-entfernen-ebene, .kmvs-ausschnitt-ebene, .kmvs-kommentar-ebene")) return;
            const seitenDiv = ziel.closest<HTMLElement>(".page[data-page-number]");
            if (!seitenDiv) return;
            // Über Text wählt man Text aus — außer mit Alt.
            const aufText = !!ziel.closest(".textLayer span") && !ziel.classList.contains("endOfContent");
            if (aufText && !e.altKey) return;
            const seite = Number(seitenDiv.dataset.pageNumber) - 1;
            if (!seitenAnsicht(seite)) return;
            e.preventDefault();
            const box = inhaltsBox(seitenDiv);
            const klemme = (v: number, max: number) => Math.min(Math.max(v, 0), max);
            const start = { x: e.clientX - box.left, y: e.clientY - box.top };
            let aktuell: Zug = { seite, x0: start.x, y0: start.y, x1: start.x, y1: start.y };
            setZug(aktuell);
            const onMove = (ev: PointerEvent) => {
                const p = einrasten(start.x, start.y, klemme(ev.clientX - box.left, box.width), klemme(ev.clientY - box.top, box.height));
                aktuell = { seite, ...p };
                setZug(aktuell);
            };
            const beenden = () => {
                alleFenster.removeEventListener("pointermove", onMove);
                alleFenster.removeEventListener("pointerup", onUp);
                alleFenster.removeEventListener("pointercancel", beenden);
                zugBeenden = null;
                setZug(null);
            };
            const onUp = (ev: PointerEvent) => {
                onMove(ev);
                beenden();
                if (Math.hypot(aktuell.x1 - aktuell.x0, aktuell.y1 - aktuell.y0) < MIN_LAENGE) return;
                const ansicht = seitenAnsicht(seite);
                if (!ansicht) return;
                dispatch({
                    typ: "streichenHinzu",
                    eintraege: [{ id: neueId(), seite, farbe: farbeRef.current, quelle: "linie", ...strichAusLinie(ansicht.viewport, aktuell) }],
                });
            };
            alleFenster.addEventListener("pointermove", onMove);
            alleFenster.addEventListener("pointerup", onUp);
            alleFenster.addEventListener("pointercancel", beenden);
            zugBeenden = beenden;
        };
        container.addEventListener("pointerdown", onDown);
        return () => {
            container.removeEventListener("pointerdown", onDown);
            zugBeenden?.();
        };
    }, [aktiv, container, dispatch, seitenAnsicht, viewer]);

    // ─── Zeichnen ───────────────────────────────────────────────────────────

    if (!viewer) return null;

    const jeSeite = new Map<number, StreichEintrag[]>();
    for (const e of eintraege) jeSeite.set(e.seite, [...(jeSeite.get(e.seite) ?? []), e]);
    if (zug && !jeSeite.has(zug.seite)) jeSeite.set(zug.seite, []);
    benutzteSeiten.current = new Set(jeSeite.keys());

    return (
        <>
            {[...jeSeite].map(([seite, liste]) => {
                const ansicht = seitenAnsicht(seite);
                if (!ansicht?.div) return null;
                const v = ansicht.viewport;
                // Maßstab pt → CSS-px (unabhängig von der Drehung).
                const [ax, ay, bx, by] = v.convertToViewportRectangle([0, 0, 1, 0]);
                const massstab = Math.hypot(bx - ax, by - ay) || 1;
                const host = hostFuer(seite);
                const gezeigteGruppen = new Set<string>();
                return createPortal(
                    <>
                        <svg
                            data-streich-seite={seite}
                            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible", pointerEvents: "none" }}
                        >
                            {liste.map((e) => {
                                const [x1, y1, x2, y2] = v.convertToViewportRectangle(e.linie);
                                return (
                                    <line
                                        key={e.id}
                                        data-strich={e.id}
                                        x1={x1}
                                        y1={y1}
                                        x2={x2}
                                        y2={y2}
                                        stroke={STREICH_FARBEN[e.farbe].css}
                                        strokeWidth={Math.max(1, e.staerke * massstab)}
                                    />
                                );
                            })}
                            {zug?.seite === seite && (
                                <line
                                    data-zuglinie=""
                                    x1={zug.x0}
                                    y1={zug.y0}
                                    x2={zug.x1}
                                    y2={zug.y1}
                                    stroke={STREICH_FARBEN[farbe].css}
                                    strokeWidth={Math.max(1, LINIE_PT * massstab)}
                                    strokeDasharray="4 3"
                                />
                            )}
                        </svg>
                        {aktiv &&
                            liste.map((e) => {
                                // Das × nur einmal je Gruppe — am Ende des ersten Strichs.
                                if (e.gruppe && gezeigteGruppen.has(e.gruppe)) return null;
                                if (e.gruppe) gezeigteGruppen.add(e.gruppe);
                                const [, , x2, y2] = v.convertToViewportRectangle(e.linie);
                                return (
                                    <button
                                        key={`x-${e.id}`}
                                        type="button"
                                        aria-label="Durchstreichung zurücknehmen"
                                        title="Durchstreichung zurücknehmen"
                                        style={{ ...stil.kreuz, left: x2 + 10, top: y2 }}
                                        onPointerDown={(ev) => ev.stopPropagation()}
                                        onClick={(ev) => {
                                            ev.stopPropagation();
                                            dispatch({ typ: "streichenWeg", id: e.id });
                                        }}
                                    >
                                        ×
                                    </button>
                                );
                            })}
                    </>,
                    host,
                    `seite-${seite}`,
                );
            })}
        </>
    );
};
